import { Box, Dialog, Typography } from "@mui/material";
import { WalkthroughStyle } from "./WalkthroughDialogStyles";
import ContentShimmer from "react-content-shimmer";
import FetchAIVideoUrl from "../heygenAI/FetchAIVideoUrl";
import { useEffect, useState } from "react";
import { useVerification } from "../../../context/Verification.context";
import ToggleComponent from "../heygenAI/Toggle.component";
import { ArtifactStyle } from "./document/artifactResponse/VerficationArtifactsStyles";
import TextUnAvailable from "./document/artifactResponse/TextUnAvailable";
import AppButton from "../../base/AppButton.component";

/**
 * Insurance / Knowledge Hub walkthrough player.
 *
 * Supported video URLs:
 * - YouTube
 * - Vimeo
 * - Direct MP4
 * - Direct HLS / M3U8
 *
 * inline=true → full-page step (no MUI Dialog popup).
 *
 * Insurance WT:
 * - no top Close
 * - NEXT at the bottom
 *
 * Knowledge Hub:
 * - keeps normal close behavior
 */
const WalkthroughDialog = ({
  videoUrl,
  handleClose,
  open = true,
  walkthrough = true,
  artifact,
  isKnowledgeHub = false,
  inline = false,
}) => {
  const verification = useVerification();

  const [animateIn, setAnimateIn] = useState(false);
  const [duration, setDuration] = useState(0);
  const [direction, setDirection] = useState(null);
  const [embeddedurl, setEmbeddeurl] = useState({});
  const [showSurveyButton, setShowSurveyButton] = useState(false);
  const [loading, setLoading] = useState(true);

  /**
   * Insurance walkthrough uses bottom NEXT.
   * Knowledge Hub keeps close behavior.
   */
  const useBottomNext = walkthrough && !isKnowledgeHub;

  /**
   * Prepare walkthrough URLs.
   *
   * IMPORTANT:
   * Do NOT use normalizeDirectPlaybackUrl() here.
   *
   * normalizeDirectPlaybackUrl() rejects YouTube/Vimeo because it is
   * intended for native <video> playback.
   *
   * FetchAIVideoUrl needs the original URL so it can determine whether
   * it is:
   * - YouTube
   * - Vimeo
   * - Direct MP4
   * - Direct HLS
   */
  useEffect(() => {
    let embeddedurls;

    if (walkthrough) {
      if (isKnowledgeHub) {
        embeddedurls = {
          english_url:
            videoUrl?.knowledgehub_video_url || null,

          spanish_url:
            videoUrl?.es_knowledgehub_video_url || null,
        };
      } else {
        embeddedurls = {
          english_url:
            videoUrl?.video_url || null,

          spanish_url:
            videoUrl?.es_video_url || null,
        };
      }
    } else {
      embeddedurls = videoUrl || {};
    }

    setEmbeddeurl(embeddedurls);
  }, [videoUrl, walkthrough, isKnowledgeHub]);

  /**
   * Check availability for the selected language.
   */
  const noEnglish = !embeddedurl?.english_url;
  const noSpanish = !embeddedurl?.spanish_url;

  /**
   * Loading state.
   */
  useEffect(() => {
    if (videoUrl) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [videoUrl]);

  /**
   * Get English walkthrough text.
   */
  const getEnglishText = () => {
    if (isKnowledgeHub) {
      return videoUrl?.knowledgehub_text;
    }

    return videoUrl?.text;
  };

  /**
   * Get Spanish walkthrough text.
   */
  const getSpanishText = () => {
    if (isKnowledgeHub) {
      return videoUrl?.es_knowledgehub_text;
    }

    return videoUrl?.es_text;
  };

  /**
   * Insurance walkthrough uses NEXT button at bottom.
   */
  const nextButton = useBottomNext ? (
    <Box
      sx={{
        ...ArtifactStyle.paginationZone,
        display: "flex",
        justifyContent: "center",
        pt: 1,
      }}
    >
      <AppButton
        buttonText="NEXT"
        size="medium"
        variant="contained"
        sx={{
          width: {
            xs: "70%",
            sm: "50%",
          },
        }}
        clickHandler={handleClose}
      />
    </Box>
  ) : null;

  /**
   * Main walkthrough content.
   */
  const content = (
    <Box
      sx={{
        ...WalkthroughStyle.dialogBox,

        ...(inline
          ? {
              flex: 1,
              minHeight: 0,
              width: "100%",
              height: "100%",
              alignSelf: "stretch",
            }
          : null),
      }}
    >
      {loading ? (
        <ContentShimmer
          size={{
            height: window.innerWidth <= 750 ? 400 : 400,
            width: window.innerWidth <= 750 ? 150 : 400,
          }}
        />
      ) : !verification?.aiData?.spokeperson ? (
        /**
         * No AI spokesperson.
         *
         * Show walkthrough text instead of video.
         */
        <Box sx={ArtifactStyle.mainBox}>
          <Box sx={ArtifactStyle.toggleZone}>
            <ToggleComponent
              handleClose={handleClose}
              walkthrough={walkthrough}
              isClose={!useBottomNext}
            />
          </Box>

          {walkthrough ? (
            <Box sx={ArtifactStyle.contentBox}>
              <TextUnAvailable
                text={getEnglishText()}
                es_text={getSpanishText()}
              />
            </Box>
          ) : (
            <Box sx={ArtifactStyle.contentBox}>
              <Typography sx={WalkthroughStyle.noVideoText}>
                {!verification?.aiData?.spanish
                  ? artifact?.text
                  : artifact?.es_text}
              </Typography>
            </Box>
          )}

          {nextButton}
        </Box>
      ) : (
        /**
         * AI spokesperson exists, but selected language video
         * is not available.
         */
        verification?.aiData?.spokeperson &&
        ((noEnglish && !verification?.aiData?.spanish) ||
          (noSpanish && verification?.aiData?.spanish)) ? (
          <Box sx={ArtifactStyle.mainBox}>
            <Box sx={ArtifactStyle.toggleZone}>
              <ToggleComponent
                handleClose={handleClose}
                walkthrough={walkthrough}
                isClose={!useBottomNext}
              />
            </Box>

            <Box sx={ArtifactStyle.contentBox}>
              <Typography
                variant="h6"
                textAlign="center"
              >
                {noEnglish && !verification?.aiData?.spanish
                  ? "English video is not available."
                  : noSpanish && verification?.aiData?.spanish
                    ? "Spanish video is not available."
                    : ""}
              </Typography>
            </Box>

            {nextButton}
          </Box>
        ) : (
          /**
           * Video available.
           *
           * embeddedurl contains the ORIGINAL URL:
           *
           * YouTube:
           * https://www.youtube.com/watch?v=XXXXXXXXXXX
           *
           * Vimeo:
           * https://vimeo.com/123456789
           *
           * MP4:
           * https://example.com/video.mp4
           *
           * HLS:
           * https://example.com/video.m3u8
           *
           * FetchAIVideoUrl will determine the video type.
           */
          <Box
            sx={{
              ...ArtifactStyle.mainBox,
              width: "100%",
              height: "100%",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                overflow: "hidden",
              }}
            >
              <FetchAIVideoUrl
                handleClose={handleClose}
                videoUrl={embeddedurl}
                currentIndex={0}
                pagination={false}
                isAI={true}
                animateIn={animateIn}
                setAnimateIn={setAnimateIn}
                duration={duration}
                setDuration={setDuration}
                direction={direction}
                walkthrough={walkthrough}
                isClose={!useBottomNext}
                showSurveyButton={showSurveyButton}
                setShowSurveyButton={setShowSurveyButton}
              />
            </Box>

            {nextButton}
          </Box>
        )
      )}
    </Box>
  );

  /**
   * Inline mode.
   *
   * Used for the full-page Insurance walkthrough flow.
   */
  if (inline) {
    if (!open) {
      return null;
    }

    return content;
  }

  /**
   * Normal Dialog mode.
   */
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      PaperProps={{
        sx: {
          height: {
            xs: "100vh",
            sm: "88vh",
            md: "90vh",
          },

          maxHeight: {
            xs: "100vh",
            sm: "88vh",
            md: "90vh",
          },

          "@supports (height: 100dvh)": {
            xs: {
              height: "100dvh",
              maxHeight: "100dvh",
            },
          },

          borderRadius: {
            xs: "0px",
            sm: "6px",
            md: "6px",
            lg: "6px",
          },

          overflow: "hidden",
        },
      }}
      maxWidth="md"
      sx={{
        "& .MuiDialog-paper": {
          margin: {
            xs: 0,
          },

          width: {
            xs: "100%",
            sm: "95%",
            md: "60%",
            lg: "55%",
          },

          maxWidth: {
            xs: "100%",
            sm: "95%",
            md: "60%",
            lg: "55%",
          },
        },
      }}
    >
      {content}
    </Dialog>
  );
};

export default WalkthroughDialog;